// Types
import {
  AniseikoniaMeridianKeyType,
  AniseikoniaMeridianSummaryType,
} from "src/services/exams/types";

// Utils
import { DASH } from "../SynoptophoreGraph/shared/formatters";

/**
 * At or beyond this, a size difference is a finding rather than a reading.
 *
 * It mirrors `Constants.Resolution.SignificantPercent` in `AniseikoniaLibrary`, which is what
 * the payload's `significant` flag is computed against. It is repeated here only so the scale
 * can draw the threshold -- every per-direction judgement reads the payload's flag rather than
 * comparing against this number, so the two can never disagree about an individual reading.
 */
export const SIGNIFICANT_PERCENT = 7;

export const MERIDIANS: Array<{
  key: AniseikoniaMeridianKeyType;
  label: string;
  // What the direction is, in the words a patient would use for it.
  plainName: string;
}> = [
  { key: "horizontal", label: "Horizontal", plainName: "side to side" },
  { key: "vertical", label: "Vertical", plainName: "up and down" },
  { key: "overall", label: "Overall", plainName: "both directions at once" },
];

// How the summary names each direction in the headline sentence, which is not how the column
// titles name them -- a sentence wants words and a column title wants a label.
export const HEADLINE_NAMES: Record<AniseikoniaMeridianKeyType, string> = {
  horizontal: "Side to side",
  vertical: "Up and down",
  overall: "Overall",
};

/**
 * Which eye saw the larger image, from the sign of the difference.
 *
 * Positive names the right eye, matching `PercentDifference.Percent`. Null only at exact
 * equality: whether a difference is too small to report is `detectable`'s question, and
 * answering it here would make the eye vanish while the number stayed.
 */
export const largerEyeWord = (
  percent: number | null | undefined
): "left" | "right" | null => {
  if (percent === null || percent === undefined || percent === 0) return null;
  return percent < 0 ? "left" : "right";
};

/**
 * An elapsed time as `m:ss`, which is the one format this report uses for a duration.
 */
export const duration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return DASH;
  }

  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);

  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
};

/**
 * The direction with the largest difference, which is what the headline names.
 *
 * NOT the overall meridian. Overall is measured in its own right rather than averaged from the
 * other two, so it can read 1.0% on a patient whose side-to-side difference is 8.0% -- and a
 * headline quoting it would contradict the picture directly beneath it.
 */
export const largestDirection = (
  measured: Array<{
    key: AniseikoniaMeridianKeyType;
    summary: AniseikoniaMeridianSummaryType;
  }>
): {
  key: AniseikoniaMeridianKeyType;
  summary: AniseikoniaMeridianSummaryType;
} | null =>
  measured.reduce<{
    key: AniseikoniaMeridianKeyType;
    summary: AniseikoniaMeridianSummaryType;
  } | null>(
    (largest, candidate) =>
      largest === null ||
      Math.abs(candidate.summary.percent) > Math.abs(largest.summary.percent)
        ? candidate
        : largest,
    null
  );
